import React, { useEffect, useState } from "react";
import { api, fmtUSD, fmtNumber, fmtRelative } from "../lib/api";
import KpiCard from "../components/KpiCard";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, CartesianGrid,
  XAxis, YAxis, Tooltip, Cell, ReferenceLine,
} from "recharts";
import {
  Tag, ShieldAlert, CheckCircle2, AlertTriangle, Sparkles, Bot,
  Wrench, AlertOctagon, Clock, RefreshCw,} from "lucide-react";
import { toast } from "sonner";

// Turn the engine's DSL ("service = 'BigQuery' AND resource_name CONTAINS 'billing'")
// into plain English so the rules card stops looking like a SQL editor.
const humanizeMatch = (m) =>
  (m || "")
    .replace(/\s*=\s*/g, " is ")
    .replace(/\s+CONTAINS\s+/gi, " contains ")
    .replace(/\s+MATCHES\s+/gi, " matches ")
    .replace(/\s+AND\s+/gi, " and ")
    .replace(/\s+OR\s+/gi, " or ")
    .replace(/_/g, " ")
    .replace(/'/g, "\u201c")
    .replace(/\u201c([^\u201c]+?)\u201c/g, "\u201c$1\u201d")
    .toLowerCase()
    .replace(/^\s*(.)/, (_, c) => c.toUpperCase());

const SEV_STYLES = {
  high: "bg-[#fee2e2] text-[#b91c1c]",
  medium: "bg-[#fef3c7] text-[#92400e]",
  low: "bg-[#dbeafe] text-[#1e40af]",
};

const SERVICE_COLORS = {
  BigQuery: "#00579e", "Cloud SQL": "#15803d", "Compute Engine": "#9a3412",
  "Vertex AI": "#6d28d9", GKE: "#0e7490", "Cloud Storage": "#374151", "Cloud Run": "#be185d",
};

export default function Governance() {
  const [summary, setSummary] = useState(null);
  const [covServices, setCovServices] = useState([]);
  const [covTags, setCovTags] = useState([]);
  const [trend, setTrend] = useState([]);
  const [violations, setViolations] = useState([]);
  const [rules, setRules] = useState([]);
  const [untagged, setUntagged] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const [s, cs, ct, t, v, r, u] = await Promise.all([
        api.gov.summary(), api.gov.coverageServices(), api.gov.coverageTags(),
        api.gov.coverageTrend(30), api.gov.violations(40), api.gov.rules(),
        api.gov.untaggedSpend(25),
      ]);
      setSummary(s); setCovServices(cs); setCovTags(ct); setTrend(t);
      setViolations(v); setRules(r); setUntagged(u);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); const id = setInterval(load, 12000); return () => clearInterval(id); }, []);

  const refresh = async () => { setRefreshing(true); await load(); setRefreshing(false); toast.success("Coverage refreshed"); };

  const toggle = async (rule) => {
    try {
      await api.gov.toggleRule(rule.id, !rule.enabled);
      toast.success(`${rule.name} ${!rule.enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error("Toggle failed"); }
  };

  if (!summary) {
    return <div className="px-6 lg:px-10 py-10 text-muted-foreground">Loading governance dashboard…</div>;
  }

  return (
    <div className="px-6 lg:px-10 py-8 space-y-6" data-testid="governance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Auto-tagging policy engine, coverage analytics & policy enforcement across every connected cloud.
        </p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="w-3.5 h-3.5" />
          Last sweep {fmtRelative(summary.last_sweep_at)} · Next sweep {fmtRelative(summary.next_sweep_at)}
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} className="rounded-full h-7 ml-2" data-testid="gov-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "cs-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard testId="gov-kpi-coverage" label="Tag coverage" value={`${summary.coverage_pct}%`}
                 sublabel={`${fmtNumber(summary.tagged_resources)} of ${fmtNumber(summary.total_resources)} resources`}
                 icon={Tag} tone={summary.coverage_pct > 80 ? "success" : "warning"} />
        <KpiCard testId="gov-kpi-untagged" label="Untagged resources" value={fmtNumber(summary.untagged_resources)}
                 sublabel={`${fmtUSD(summary.untagged_cost_at_risk_usd)} at risk`}
                 icon={AlertOctagon} tone="danger" />
        <KpiCard testId="gov-kpi-auto-tag" label="Auto-tagged" value={fmtNumber(summary.auto_tagged)}
                 sublabel={`${summary.auto_tag_share_pct}% of tagged`}
                 icon={Bot} tone="primary" />
        <KpiCard testId="gov-kpi-rules" label="Active rules" value={`${summary.active_rules} / ${summary.total_rules}`}
                 sublabel={`${fmtNumber(summary.applications_run_24h)} tags applied 24h`}
                 icon={Wrench} tone="teal" />
        <KpiCard testId="gov-kpi-violations" label="Policy violations" value={fmtNumber(summary.policy_violations)}
                 sublabel="Open · awaiting fix" icon={ShieldAlert}
                 tone={summary.policy_violations > 50 ? "danger" : "warning"} />
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="bg-white border border-border rounded-full p-1 h-auto">
          {[
            { v: "overview", l: "Coverage Overview" },
            { v: "rules",    l: "Auto-Tagging Rules" },
            { v: "violations", l: "Violations" },
            { v: "untagged", l: "Untagged Spend" },
          ].map(t => (
            <TabsTrigger key={t.v} value={t.v} data-testid={`gov-tab-${t.v}`}
              className="rounded-full data-[state=active]:bg-[#00579e] data-[state=active]:text-white text-xs px-4 py-1.5">
              {t.l}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="cs-card p-6" data-testid="gov-trend-chart">
              <h3 className="cs-heading text-base font-semibold tracking-tight">Coverage Trend</h3>
              <p className="text-xs text-muted-foreground mt-0.5 mb-3">% tagged + % auto-tagged over time</p>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#6b7280" interval="preserveStartEnd" minTickGap={40} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} stroke="#6b7280" domain={[40, 100]} tickFormatter={(v) => `${v}%`} />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => `${v}%`} />
                    <ReferenceLine y={80} stroke="#22c55e" strokeDasharray="4 4" label={{ value: "Target 80%", fill: "#22c55e", fontSize: 10, position: "right" }} />
                    <Line type="monotone" dataKey="coverage_pct" name="Coverage" stroke="#00579e" strokeWidth={2.5} dot={false} />
                    <Line type="monotone" dataKey="auto_tag_pct" name="Auto-tagged" stroke="#48b1e4" strokeWidth={2} strokeDasharray="3 3" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

              <div className="cs-card p-6" data-testid="gov-services-chart">
                <h3 className="cs-heading text-base font-semibold tracking-tight">Coverage by Service</h3>
                <p className="text-xs text-muted-foreground mt-0.5 mb-3">Which GCP services have the worst tagging hygiene</p>
                <div className="space-y-2.5">
                  {covServices.map(s => (
                    <div key={s.service} className="flex items-center gap-3" data-testid={`svc-cov-${s.service.replace(/\s+/g, '-')}`}>
                      <div className="w-28 shrink-0 text-xs text-foreground truncate">{s.service}</div>
                      <div className="flex-1 h-5 bg-[hsl(var(--cs-tint))] rounded-full overflow-hidden">
                        <div className="h-full rounded-full flex items-center justify-end pr-2"
                             style={{ width: `${s.coverage_pct}%`, background: SERVICE_COLORS[s.service] || "#6b7280", minWidth: "44px" }}>
                          <span className="text-[10px] font-semibold text-white">{s.coverage_pct}%</span>
                        </div>
                      </div>
                      <div className="w-24 text-right text-[11px] text-muted-foreground cs-mono shrink-0">
                        {fmtNumber(s.tagged)} / {fmtNumber(s.total)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
          </div>

          <div className="cs-card p-6" data-testid="gov-tag-coverage">
            <h3 className="cs-heading text-base font-semibold tracking-tight">Coverage by Required Tag</h3>
            <p className="text-xs text-muted-foreground mt-0.5 mb-4">% of resources carrying each required tag (from taxonomy)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {covTags.map(t => (
                <div key={t.key} className="border border-border rounded-lg p-3" data-testid={`tag-coverage-${t.key}`}>
                  <div className="flex justify-between items-center mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="cs-mono text-[11px] font-semibold text-foreground">{t.key}</span>
                      {t.required && <span className="cs-pill bg-[#fee2e2] text-[#b91c1c] text-[9px]">required</span>}
                    </div>
                    <span className="cs-mono text-xs font-semibold">{t.coverage_pct}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-[hsl(var(--cs-tint))] rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{
                      width: `${t.coverage_pct}%`,
                      background: t.coverage_pct >= 80 ? "#22c55e" : t.coverage_pct >= 60 ? "#f59e0b" : "#ef4444",
                    }} />
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {fmtNumber(t.tagged_resources)} tagged · {fmtNumber(t.untagged_resources)} missing
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* RULES */}
        <TabsContent value="rules">
          <div className="cs-card overflow-hidden" data-testid="gov-rules-card">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="cs-heading text-base font-semibold tracking-tight">Auto-Tagging Rules</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Match expression → apply tags. Runs every 30 minutes after every sync.</p>
              </div>
              <span className="text-xs text-muted-foreground">
                <span className="text-foreground font-medium">{rules.filter(r => r.enabled).length}</span> / {rules.length} enabled
              </span>
            </div>
            <div className="divide-y divide-border">
              {rules.map(r => (
                <div key={r.id} className="px-6 py-4" data-testid={`gov-rule-${r.id}`}>
                  <div className="flex items-start gap-4">
                    <Switch checked={r.enabled} onCheckedChange={() => toggle(r)} data-testid={`toggle-${r.id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="cs-heading text-sm font-semibold">{r.name}</h4>
                        <span className="cs-pill bg-[#f3f4f6] text-[#374151] text-[10px]">{r.scope}</span>
                      </div>
                      <div className="mt-1.5 text-[12px] text-muted-foreground">
                        Matches <span className="text-[#00579e] font-medium">{humanizeMatch(r.match)}</span>
                      </div>
                      <div className="mt-1 cs-mono text-[11px] flex flex-wrap gap-1.5">
                        {Object.entries(r.apply).map(([k, v]) => (
                          <span key={k} className="bg-[#dbeafe] text-[#00579e] rounded px-1.5 py-0.5">
                            {k}=<span className="font-semibold">{String(v)}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right text-xs shrink-0">
                      <div className="cs-mono font-semibold">{fmtNumber(r.applied_count)}</div>
                      <div className="text-[10px] text-muted-foreground">tags applied</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* VIOLATIONS */}
        <TabsContent value="violations">
          <div className="cs-card overflow-hidden" data-testid="gov-violations-card">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="cs-heading text-base font-semibold tracking-tight">Policy Violations</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Resources that fail tag policy · sorted by detection time</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
                  <tr className="text-left">
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Sev</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Violation</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Resource</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Service · Project</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Tag</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Monthly cost</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Detected</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {violations.map(v => (
                    <tr key={v.id} className="cs-row-hover border-b border-border last:border-0" data-testid={`violation-${v.id}`}>
                      <td className="px-4 py-3"><span className={`cs-pill ${SEV_STYLES[v.severity]}`}>{v.severity}</span></td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground text-xs">{v.type_label}</div>
                      </td>
                      <td className="px-4 py-3 cs-mono text-[11px] text-muted-foreground">{v.resource_name}</td>
                      <td className="px-4 py-3 text-[11px] text-muted-foreground">{v.service}<br/>{v.project_id}</td>
                      <td className="px-4 py-3 cs-mono text-[11px]">{v.tag_key}</td>
                      <td className="px-4 py-3 text-right cs-mono font-semibold">{fmtUSD(v.estimated_monthly_cost_usd)}</td>
                      <td className="px-4 py-3 text-[11px] text-muted-foreground whitespace-nowrap">{fmtRelative(v.detected_at)}</td>
                      <td className="px-4 py-3">
                        {v.auto_fixable ? (
                          <Button size="sm" className="rounded-full h-7 bg-[#00579e] hover:bg-[#004580] text-white text-xs px-3" data-testid={`fix-${v.id}`}>
                            <Sparkles className="w-3 h-3 mr-1" /> Auto-fix
                          </Button>
                        ) : <span className="text-[11px] text-muted-foreground">Manual</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* TOP UNTAGGED SPEND */}
        <TabsContent value="untagged">
          {!untagged ? (
            <div className="cs-card p-10 text-center text-muted-foreground">Loading untagged spend…</div>
          ) : (
            <div className="space-y-4" data-testid="gov-untagged-spend-section">
              {/* Summary strip */}
              <div className="cs-card p-5 flex flex-wrap items-center gap-6" data-testid="untagged-summary-strip">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Monthly $ at risk</div>
                  <div className="cs-kpi-value text-2xl text-[#b91c1c] mt-1" data-testid="untagged-total-risk">
                    {fmtUSD(untagged.total_monthly_cost_at_risk_usd)}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    across {untagged.rows.length} top resources
                  </div>
                </div>
                <div className="h-10 w-px bg-border" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Auto-fixable</div>
                  <div className="cs-kpi-value text-2xl text-[#15803d] mt-1" data-testid="untagged-autofix-amount">
                    {fmtUSD(untagged.auto_fixable_monthly_cost_usd)}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {untagged.auto_fixable_share_pct}% via existing rules
                  </div>
                </div>
                <div className="h-10 w-px bg-border" />
                <div className="flex-1 min-w-[260px]">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5">$ at risk by missing tag</div>
                  <div className="flex flex-wrap gap-1.5">
                    {untagged.by_tag.slice(0, 6).map(t => (
                      <span key={t.tag_key} className="cs-pill bg-[#fef3c7] text-[#92400e]" data-testid={`untagged-bytag-${t.tag_key}`}>
                        <span className="cs-mono font-semibold">{t.tag_key}</span>
                        <span className="text-[10px] opacity-75">{t.resources}</span>
                        <span className="font-semibold">{fmtUSD(t.monthly_cost_usd, { compact: true })}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Top untagged resources table */}
              <div className="cs-card overflow-hidden" data-testid="gov-untagged-table-card">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                  <div>
                    <h3 className="cs-heading text-base font-semibold tracking-tight">Top Resources Missing Required Tags</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Ranked by monthly spend at risk · auto-fixable rows can be remediated by existing tagging rules</p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
                      <tr className="text-left">
                        <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Resource</th>
                        <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Service</th>
                        <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Project</th>
                        <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Missing Tags</th>
                        <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Severity</th>
                        <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">$ / month</th>
                        <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {untagged.rows.map(row => (
                        <tr key={row.id} className="cs-row-hover border-b border-border last:border-0" data-testid={`untagged-row-${row.id}`}>
                          <td className="px-4 py-3 cs-mono text-xs font-medium text-foreground">{row.resource_name}</td>
                          <td className="px-4 py-3">
                            <span className="cs-pill" style={{ background: (SERVICE_COLORS[row.service] || "#6b7280") + "1a", color: SERVICE_COLORS[row.service] || "#374151" }}>
                              {row.service}
                            </span>
                          </td>
                          <td className="px-4 py-3 cs-mono text-[11px] text-muted-foreground">{row.project_id}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {row.missing_tags.map(k => (
                                <span key={k} className="cs-mono text-[10px] px-1.5 py-0.5 rounded bg-[#fee2e2] text-[#b91c1c]">{k}</span>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`cs-pill ${SEV_STYLES[row.severity]}`}>{row.severity}</span>
                          </td>
                          <td className="px-4 py-3 text-right cs-mono font-semibold text-[#b91c1c]">{fmtUSD(row.monthly_cost_usd)}</td>
                          <td className="px-4 py-3">
                            {row.auto_fixable ? (
                              <button
                                onClick={() => toast.success("Auto-tag queued", { description: `${row.resource_name} → rule ${row.suggested_rule_id}` })}
                                data-testid={`untagged-autofix-${row.id}`}
                                className="cs-pill bg-[#dcfce7] text-[#15803d] hover:bg-[#bbf7d0] cursor-pointer"
                              >
                                <Bot className="w-3 h-3" /> Auto-fix
                              </button>
                            ) : (
                              <span className="cs-pill bg-[#f3f4f6] text-[#6b7280]">
                                <Wrench className="w-3 h-3" /> Manual
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
